/**
 * CDP SDK Core Types
 * All TypeScript interfaces for the plugin-based SDK architecture
 */

// =============================================================================
// CONFIGURATION
// =============================================================================

export interface CDPCoreConfig {
  /** CDP API host URL (e.g., 'https://api.yourcdp.com') */
  apiHost: string;
  /** Write key for authentication */
  writeKey: string;
  /** Enable debug logging (default: false) */
  debug?: boolean;
  /** Cookie domain for cross-subdomain tracking (e.g., '.example.com') */
  cookieDomain?: string;
  /** Cookie expiration in days (default: 365) */
  cookieExpiration?: number;
  /** Session timeout in milliseconds (default: 30 minutes) */
  sessionTimeout?: number;
  /** Auto-track page views on init and navigation (default: true) */
  autoTrack?: boolean;
  /** Ignored since 2026-10-20: the SDK computes no device fingerprint in any
   *  mode (monfri decision ЛО-13). Kept so existing snippets still type-check. */
  enableFingerprint?: boolean;
  /** Require explicit consent before sending events (default: true).
   *  When false, all consent categories are auto-granted on init,
   *  allowing events to flow without a consent banner. */
  requireConsent?: boolean;
  /** Client-side event batching. Events are buffered and sent together via
   *  POST /v1/events/batch instead of one request per event — a single article
   *  view fires ~9 events, so batching cuts request volume ~9x. Flushes when
   *  the buffer reaches batchSize, after flushInterval ms, or on page hide.
   *  Set batchSize to 1 (or 0) to disable and send each event immediately.
   *  (default: 10) */
  batchSize?: number;
  /** Max ms an event waits in the batch buffer before a time-based flush
   *  (default: 3000). Ignored when batchSize <= 1. */
  flushInterval?: number;
  /** Fetch the remote-managed Site Profile from {apiHost}/v1/config at init
   *  (default: true). The profile carries per-site selectors, URL→category
   *  taxonomy, event toggles, consent mode and privacy settings, managed
   *  centrally in the dashboard. Disable to run purely on inline config. */
  remoteConfig?: boolean;
  /** Inline Site Profile override. Merged OVER the remote profile, so it wins
   *  — useful for testing or an emergency override without a dashboard change.
   *  Normally left unset (the snippet stays minimal; config lives remotely). */
  siteProfile?: SiteProfile;
}

export const DEFAULT_CONFIG: Partial<CDPCoreConfig> = {
  debug: false,
  cookieExpiration: 365,
  sessionTimeout: 30 * 60 * 1000, // 30 minutes
  autoTrack: true,
  enableFingerprint: false,
  requireConsent: true,
  batchSize: 10,
  flushInterval: 3000,
  remoteConfig: true,
};

// =============================================================================
// SITE PROFILE (remote-managed, per-workspace tracking configuration)
// =============================================================================

/**
 * How the SDK obtains consent before tracking with a persistent identity.
 * - `anonymous-until-consent` (default for news): pre-consent runs in a lite,
 *   cookieless mode (no persistent ID, IP truncated server-side, no
 *   fingerprint) emitting aggregate measurement only; on consent it upgrades to
 *   full identified tracking.
 * - `strict`: nothing is sent until the visitor opts in.
 * - `cmp-only`: do exactly what the site's CMP / cdp.consent() says, no
 *   independent posture.
 * - `implied`: everything on by default (legacy `requireConsent:false`); only
 *   for non-EU or where a separate lawful basis exists.
 */
export type ConsentMode =
  | 'anonymous-until-consent'
  | 'strict'
  | 'cmp-only'
  | 'implied';

/** The old active-fingerprinting posture. Not read since 2026-10-20 (ЛО-13): every
 *  value means `off` — no canvas, WebGL or audio fingerprint is computed. */
export type FingerprintMode = 'off' | 'passive' | 'full';

/**
 * One URL→category rule. `pattern` is matched against the URL path (and full
 * URL) — supports a leading-glob form ("/sport/*"), a plain substring, or a
 * `re:` prefix for a regular expression ("re:^/\\d+/sport/").
 */
export interface UrlTaxonomyRule {
  pattern: string;
  category?: string;
  section?: string;
  /** Optional content classification, e.g. 'article' | 'video' | 'gallery'. */
  contentType?: string;
}

/** Which news events the SDK emits (lean mode). */
export interface SiteEventsConfig {
  /** Emit the 4 separate "Article Scroll Milestone" events. Default false —
   *  the milestones (with per-step timing) are folded into the single
   *  "Article Reading Session" event instead, cutting ~4 events/article. */
  scrollMilestones?: boolean;
  /** Suppress the generic core `page` event on detected article pages, since
   *  "Article Viewed" already carries the same context. Default true. */
  suppressPageOnArticle?: boolean;
  /** Emit "Article Engaged" (default true — drives bounce rate / engagement). */
  trackEngaged?: boolean;
  /** Emit "Article Read" (default true — drives read rate / recommendations). */
  trackRead?: boolean;
  /** Track share-button clicks (default true). */
  trackShares?: boolean;
  /** Send the FULL context only on the first event of each page view; every
   *  event after it carries only the fields the server actually reads
   *  (page.url/path/referrer, session.id, device.id, screen, automation,
   *  campaign, emailClickId). Default false.
   *
   *  Measured 2026-09-21 (SDK-EVENT-PIPELINE §S2): the full context is
   *  1 119-1 361 B per event, ~600 B of which has NO reader at all — the
   *  server overwrites `userAgent` from the HTTP header and re-parses
   *  `browser`/`os` from that same header, and nothing reads `library`,
   *  `page.title` or five of the six `session` fields. The worker then writes
   *  `context: '{}'` to ClickHouse anyway.
   *
   *  `screen` and `automation` are deliberately KEPT in the lean shape even
   *  though they are only read by the bot heuristics: dropping them would let
   *  a headless browser fail detectClientAnomaly on every event after its
   *  first. 140 B against a silent hole in bot detection is a bad trade. */
  leanContext?: boolean;
  /** 'off' stops the news plugin from collecting `properties.tags` at all.
   *
   *  For a site whose article markup carries the SITE's SEO keywords rather
   *  than the article's (24chasa: one 50-keyword list across 1.2 M events a
   *  day, M32), the tags are not information — they are 756 B per event that
   *  gives every reader the same ~49 "interests". The server already caps and
   *  strips them at ingest; this stops them leaving the reader's browser. */
  collectTags?: 'on' | 'off';
  /** Drop the property keys that no consumer in the platform reads
   *  (`section`, `language`, `isPremium`, `siteId`, `checkpointSeq`,
   *  `scrollDownCount`, `readingTimeMinutes`) — verified against all seven
   *  repos 2026-09-21, SDK-EVENT-PIPELINE §S6. Default false.
   *
   *  `articleType` is NOT in that list even though nothing reads it either:
   *  news-traits reads `contentType`, which the SDK has never sent, so the
   *  field is the raw material for fixing that (§S13) rather than dead. */
  leanProperties?: boolean;
}

export interface SiteConsentConfig {
  mode?: ConsentMode;
  /** Bridge an IAB TCF v2.2 CMP (window.__tcfapi) if present. Default true. */
  tcf?: boolean;
  /** Honor Global Privacy Control + Do-Not-Track signals. Default true. */
  honorGpcDnt?: boolean;
  /**
   * Show the Monfri consent banner when the site has no consent manager of its
   * own and the visitor has not decided yet (СТ22 — on by default for sites
   * created from 07.10.2026). With a CMP on the page the decision comes from it.
   */
  banner?: boolean;
  /** The site's privacy/cookie page, linked from the banner. */
  policyUrl?: string;
  /** The choice the site keeps itself (its own banner) — MEDIA-COMMERCE-2026-10-29 §1.3. */
  bridge?: SiteConsentBridge;
}

/**
 * A site's own consent choice, read on every page (market.dir.bg: `mw_consent` in localStorage, `necessary` = no).
 * A value in `deny` refuses everything, in `grant` (or any other value when `grant` is absent) allows everything; no
 * value leaves the site's mode in charge. Applied for the page only — never written to `cdp_consent`, which is shared
 * by every site on the cookie domain.
 */
export interface SiteConsentBridge {
  /** localStorage key, then a cookie of the same name. */
  storageKey?: string;
  /** A dotted path to a function on window that returns the value (`mwConsent.get`). Tried before storageKey. */
  global?: string;
  deny?: string[];
  grant?: string[];
}

/** E-commerce on a site without one of the named platform plugins (MEDIA-COMMERCE-2026-10-29 §1.4). */
export interface SiteCommerceConfig {
  /** Read the store's Google `dataLayer` (UA Enhanced Ecommerce and GA4). On by default with `platform: 'custom'`. */
  dataLayer?: boolean;
  /** The dataLayer's global name. Default `dataLayer`. */
  dataLayerName?: string;
  /** Convert BGN amounts to EUR at 1.95583 (Bulgaria's euro, 01.01.2026). Default true. */
  convertBgn?: boolean;
  /** Identify the signed-in customer from the dataLayer `UserDetails` push. Default true. */
  identify?: boolean;
}

export interface SitePrivacyConfig {
  /** Server-side IP anonymization for this site. Surfaced here so the
   *  dashboard editor shows it; enforced on the server. Default true (news). */
  anonymizeIp?: boolean;
  /** Not read since 2026-10-20 — no mode fingerprints (ЛО-13); the server serves
   *  'off' here so that bundles from before still stop. */
  fingerprint?: FingerprintMode;
  /** Pages of this site that send NOTHING (Р-ID8) — globs on the path (`/profil/parola`), or on host + path
   *  when the pattern starts with a host (`profile.dir.bg/reset/*`). Added to the built-in password pages
   *  (core/url-scrub.ts `isSensitivePath`). */
  excludePaths?: string[];
}

/**
 * The per-workspace Site Profile. Stored server-side in
 * `workspace.settings.siteProfile`, served by GET /v1/config, cached in the
 * browser, and edited centrally in the dashboard. The profile describes the
 * workspace's main site; its other sites (a media's shop) are entries in
 * `domainOverrides`. Workspaces are isolated — no identity across them.
 */
export interface SiteProfile {
  /** Bumped on each dashboard save; used to bust the browser cache. */
  version?: number;
  domain?: string;
  siteType?: 'news' | 'ecommerce' | 'general';
  /** Declared platform (woocommerce/shopify/magento/cloudcart/...). When set,
   *  the full bundle activates exactly this platform's plugin instead of
   *  DOM-sniffing. */
  platform?: string;
  /** Forces content language (e.g. 'bg') when the page markup is unreliable. */
  language?: string;
  /** Cookie domain to unify subdomains, e.g. '.24chasa.bg'. */
  cookieDomain?: string;
  /** Article selector overrides (ArticleSelectors shape; kept loose to avoid
   *  coupling core to the news plugin types). */
  selectors?: Record<string, string>;
  /** URL→category rules — the primary category source for sites (like
   *  24chasa.bg) whose category lives in the URL path, not the markup. */
  urlTaxonomy?: UrlTaxonomyRule[];
  /** Slug→display-name fallback applied to an extracted category. */
  categoryMap?: Record<string, string>;
  events?: SiteEventsConfig;
  consent?: SiteConsentConfig;
  privacy?: SitePrivacyConfig;
  /** The workspace's other sites, by hostname — each may carry its own `siteType`/`platform` (a media and its shop,
   *  MEDIA-COMMERCE-2026-10-29 §1.1), merged over this profile on that host. */
  domainOverrides?: Record<string, Partial<SiteProfile>>;
  /** E-commerce without a named platform plugin — the dataLayer bridge. */
  commerce?: SiteCommerceConfig;
  /** Push web-app pointer + opt-in banner presentation, served by /v1/config.
   *  `appId`/`host` are set by the Hub Domain fan-out
   *  (POST /api/internal/sites/push-app); the copy/theme fields are optional
   *  and edited in the dashboard. This is what lets a new customer get a
   *  branded push banner with NO SDK release — the thing the hardcoded
   *  HOST_PRESETS map in plugins/push used to require. */
  push?: {
    appId?: number;
    host?: string;
    title?: string;
    message?: string;
    acceptText?: string;
    denyText?: string;
    icon?: string;
    color?: string;
  };
  /** Recommendation placements to auto-inject, embedded in /v1/config so the
   *  reco plugin needs no second fetch. Absent = reco not configured. */
  reco?: RecoManifest;
  /** The newsletter form under the article (monfri ОБ-Р33): written by the CDP when the owner switches the newsletter
   *  on; the form posts to the site's Push app. Absent or not enabled = no form. */
  newsletter?: { enabled?: boolean; appId?: number; pushUrl?: string; language?: string; color?: string };
  /** Recognising the site's signed-in readers (NEWS-LOGIN-IDENTITY-2026-10-26 §5) — written by us in CDP, nothing on the
   *  site. Absent or not enabled = the module does nothing. core/auth-identity.ts. */
  identity?: SiteIdentityConfig;
}

/** NEWS-LOGIN-IDENTITY-2026-10-26 §5.4. Patterns are globs on the path (`/login`), or on host + path when they start with a
 *  host (`profile.dir.bg/profile`); `*` is every page. */
export interface SiteIdentityConfig {
  enabled?: boolean;
  /** 'observe' sends `Auth Detected {source, stage}` instead of any e-mail — a technical check. Default 'on'. */
  mode?: 'observe' | 'on';
  /** Present only while a reader is signed in (dir.bg `#loggedInUser.logged` after its /init). */
  loggedIn?: { selector: string; waitMs?: number };
  /** The login form: its e-mail is held server-side until the next page shows `loggedIn` (§5.2). */
  forms?: Array<{ match: string; kind?: 'login'; emailField: string }>;
  /** A page that shows the signed-in reader's e-mail (verified). `fetchUrl` (same origin only): read that page in the
   *  background ONCE per browser when `loggedIn` shows and the browser is not identified yet (Р-ID10). */
  pageData?: Array<{ match: string; email: string; fetchUrl?: string }>;
  /** A click (or form submit) here signs the reader out → `reset()`. */
  logout?: { selector: string };
}

/** A placement the reco plugin auto-injects on the page (mirrors the reco-engine
 *  /v1/reco/_manifest shape). */
export interface RecoPlacementManifest {
  slug: string;
  type: 'articles' | 'products';
  /** The bound strategy starts from the viewed item → the plugin supplies data-item. */
  needs_item?: boolean;
  targeting: {
    url_pattern?: string | null;
    css_selector: string;
    insert_mode?: 'before' | 'after' | 'prepend' | 'append' | 'replace';
    nth_repeat?: number | null;
    max_injections?: number | null;
    device?: 'all' | 'desktop' | 'mobile';
    /** Shadow-DOM isolation (default true). */
    shadow?: boolean;
    /** Reserved px height before load (CLS prevention). */
    reserved_height?: number | null;
  };
}
export interface RecoManifest {
  placements: RecoPlacementManifest[];
}

// =============================================================================
// CONSENT
// =============================================================================

export interface ConsentPreferences {
  /** Analytics tracking (page views, custom events) */
  analytics?: boolean;
  /** Marketing tracking (e-commerce events, ad attribution) */
  marketing?: boolean;
  /** Personalization (identify, user traits) */
  personalization?: boolean;
  /** Functional (always enabled, basic site operation) */
  functional?: boolean;
}

export type ConsentType = 'analytics' | 'marketing' | 'personalization' | 'functional';

// =============================================================================
// CONTEXT
// =============================================================================

export interface PageContext {
  path: string;
  url: string;
  title: string;
  referrer: string;
  search?: string;
}

export interface ScreenContext {
  width: number;
  height: number;
  density: number;
  innerWidth?: number;
  innerHeight?: number;
}

export interface BrowserInfo {
  name: string;
  version: string;
}

export interface OSInfo {
  name: string;
  version?: string;
}

export interface DeviceInfo {
  id: string;
  type: 'desktop' | 'mobile' | 'tablet';
  fingerprint?: FingerprintComponents;
  confidence?: number;
}

export interface CampaignContext {
  name?: string | null;
  source?: string | null;
  medium?: string | null;
  term?: string | null;
  content?: string | null;
}

export interface SessionContext {
  id: string;
  pageViews: number;
  start: number;
  lastActivity: number;
  referrer?: string;
  landingPage?: string;
}

export interface LibraryInfo {
  name: string;
  version: string;
}

export interface EventContext {
  page: PageContext;
  screen: ScreenContext;
  browser: BrowserInfo;
  os: OSInfo;
  device?: DeviceInfo;
  locale: string;
  timezone: string;
  userAgent: string;
  campaign?: CampaignContext;
  session: SessionContext;
  library: LibraryInfo;
  // Sticky email-click attribution. Set when the visitor lands with
  // ?_pe=<event_id> (push-tracking redirect) and persists in storage
  // for 90 days so an order placed days later still attributes back.
  emailClickId?: string | null;
  // Lightweight headless / automation tells collected client-side for server-side
  // bot detection: navigator.webdriver, a missing window.chrome on a Chrome UA,
  // and plugins/languages counts (headless Chrome reports 0). -1 = unknown.
  automation?: AutomationSignals;
}

export interface AutomationSignals {
  webdriver: boolean;
  noChrome: boolean;
  plugins: number;
  languages: number;
}

// =============================================================================
// FINGERPRINT
// =============================================================================

export interface FingerprintComponents {
  screenResolution: string;
  availableResolution: string;
  colorDepth: number;
  pixelRatio: number;
  timezone: string;
  timezoneOffset: number;
  language: string;
  languages: string[];
  platform: string;
  vendor: string;
  hardwareConcurrency: number;
  deviceMemory?: number;
  maxTouchPoints: number;
  cookiesEnabled: boolean;
  doNotTrack: string | null;
  canvas?: string;
  webgl?: string;
  webglVendor?: string;
  webglRenderer?: string;
  audio?: string;
}

export interface DeviceFingerprint {
  id: string;
  components: FingerprintComponents;
  confidence: number;
  createdAt: number;
}

// =============================================================================
// SESSION
// =============================================================================

export interface SessionData {
  id: string;
  start: number;
  lastActivity: number;
  pageViews: number;
  referrer: string;
  landingPage: string;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmTerm?: string | null;
  utmContent?: string | null;
  // Email-marketing attribution: when push-tracking redirects from a
  // tracked email click it appends `?_pe=<event_id>`. SDK reads it on
  // first page load and stores both on the session (short TTL) and
  // in a long-lived `cdp_email_click_id` storage key so a returning
  // visitor's order can still attribute back to the email after the
  // session expires. Surfaced on every event payload as
  // `properties.email_click_id`.
  emailClickId?: string | null;
}

// =============================================================================
// EVENTS
// =============================================================================

export type EventType = 'page' | 'track' | 'identify' | 'group';

export interface BaseEventPayload {
  type: EventType;
  anonymousId: string | null;
  userId: string | null;
  timestamp: string;
  messageId: string;
  context: EventContext;
}

export interface PageEventPayload extends BaseEventPayload {
  type: 'page';
  name?: string;
  category?: string;
  properties?: Record<string, unknown>;
}

export interface TrackEventPayload extends BaseEventPayload {
  type: 'track';
  event: string;
  properties?: Record<string, unknown>;
}

export interface IdentifyEventPayload extends BaseEventPayload {
  type: 'identify';
  traits?: Record<string, unknown>;
}

export interface GroupEventPayload extends BaseEventPayload {
  type: 'group';
  groupId: string;
  traits?: Record<string, unknown>;
}

export type EventPayload =
  | PageEventPayload
  | TrackEventPayload
  | IdentifyEventPayload
  | GroupEventPayload;

// =============================================================================
// QUEUE
// =============================================================================

export interface QueuedEvent {
  payload: EventPayload;
  attempts: number;
  lastAttempt: number;
  createdAt: number;
}

// =============================================================================
// TRANSPORT
// =============================================================================

export interface RetryConfig {
  maxRetries: number;
  baseDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 30000,
  backoffMultiplier: 2,
};

// =============================================================================
// PLUGIN SYSTEM
// =============================================================================

export interface CDPPlugin {
  /** Unique plugin identifier */
  readonly name: string;
  /** Plugin version */
  readonly version: string;

  /** Called when plugin is registered with core */
  install(core: CDPCoreAPI): void;

  /** Called when plugin is unregistered */
  uninstall?(): void;

  /** Called after core initialization completes */
  onInit?(config: CDPCoreConfig): void;

  /** Intercept events before sending - return null to cancel */
  beforeSend?(payload: EventPayload): EventPayload | null;

  /** Called after event is sent */
  afterSend?(payload: EventPayload, success: boolean): void;

  /** Called on page unload (pagehide/beforeunload) - use for cleanup or beacon events */
  onUnload?(): void;

  /**
   * Called when the tab is hidden (visibilitychange→hidden). The page is NOT
   * going away — it may come back. Use it to checkpoint/flush, never to tear
   * down. Plugins that do not implement it fall back to onUnload (the
   * historical behaviour), which is right for one-shot roll-ups and wrong for
   * anything that must keep working after the tab returns.
   */
  onHidden?(): void;

  /** Called on page navigation (SPA) */
  onPageChange?(url: string): void;

  /** Allow plugin-specific methods */
  [key: string]: unknown;
}

// =============================================================================
// CORE API (exposed to plugins)
// =============================================================================

export interface CDPCoreAPI {
  // Initialization
  init(writeKey: string, options?: Partial<CDPCoreConfig>): void;
  isInitialized(): boolean;
  getConfig(): CDPCoreConfig | null;
  /** The resolved Site Profile (defaults ← remote ← inline). Plugins read this
   *  for selectors, URL taxonomy, language and event toggles. */
  getSiteProfile(): SiteProfile;
  /** Resolves when the freshest Site Profile is available (immediately on a
   *  warm cache or when remoteConfig is off; bounded by the fetch timeout on a
   *  cold start). Plugins await this before first use only when the profile is
   *  still missing site-specific data. */
  whenConfigReady(): Promise<SiteProfile>;

  // Core tracking methods
  page(name?: string, category?: string, properties?: Record<string, unknown>): void;
  track(event: string, properties?: Record<string, unknown>): void;
  identify(userId: string, traits?: Record<string, unknown>): void;
  group(groupId: string, traits?: Record<string, unknown>): void;

  // Identity management
  getAnonymousId(): string | null;
  getUserId(): string | null;
  reset(): void;
  /** Sign-out that keeps the browser: the userId goes, the anonymous id stays (a shop's logout must not start a new
   *  reader on every site of the cookie domain — MEDIA-COMMERCE-2026-10-29 Б9). */
  forgetUser(): void;

  // Consent management
  consent(preferences: ConsentPreferences): void;
  getConsentStatus(): ConsentPreferences;
  hasConsent(type: ConsentType): boolean;
  /** Open the Monfri consent banner again (a site's "Cookie settings" link). */
  showConsentBanner(): void;

  // State access
  getContext(): Promise<EventContext>;
  getSession(): SessionData | null;
  getDeviceFingerprint(): Promise<string | null>;

  // Plugin system
  use(plugin: CDPPlugin): void;
  getPlugin<T extends CDPPlugin>(name: string): T | undefined;

  // Hooks for plugins
  onBeforeSend(callback: BeforeSendCallback): void;
  onAfterSend(callback: AfterSendCallback): void;
  onInitialized(callback: InitializedCallback): void;

  // Internal utilities (for plugins)
  log(...args: unknown[]): void;
  sendBeacon(payload: EventPayload): boolean;
}

// =============================================================================
// CALLBACKS
// =============================================================================

export type BeforeSendCallback = (payload: EventPayload) => EventPayload | null;
export type AfterSendCallback = (payload: EventPayload, success: boolean) => void;
export type InitializedCallback = () => void;

// =============================================================================
// STATE
// =============================================================================

export interface CDPState {
  initialized: boolean;
  config: CDPCoreConfig | null;
  anonymousId: string | null;
  userId: string | null;
  consent: ConsentPreferences;
  session: SessionData | null;
  deviceFingerprint: DeviceFingerprint | null;
  plugins: Map<string, CDPPlugin>;
  beforeSendCallbacks: BeforeSendCallback[];
  afterSendCallbacks: AfterSendCallback[];
  initializedCallbacks: InitializedCallback[];
  /** Resolved Site Profile (defaults ← remote ← inline). */
  siteProfile: SiteProfile;
  /** Lite (anonymous-until-consent) posture active: de-identify, no persistent
   *  ID, no fingerprint. Cleared when the visitor grants consent. */
  liteMode: boolean;
}

// =============================================================================
// STORAGE KEYS
// =============================================================================

export const STORAGE_KEYS = {
  ANONYMOUS_ID: 'cdp_anonymous_id',
  USER_ID: 'cdp_user_id',
  SESSION: 'cdp_session',
  CONSENT: 'cdp_consent',
  DEVICE_FINGERPRINT: 'cdp_device_fingerprint',
  EVENT_QUEUE: 'cdp_event_queue',
  // Cached remote Site Profile (per write key). SWR-refreshed at init.
  SITE_CONFIG: 'cdp_site_config',
  // Long-lived (90-day) email-click attribution. Survives session
  // timeout so an order placed days after the email click still
  // attributes back to that campaign. Cleared explicitly on order
  // confirmation if you want strict last-touch.
  EMAIL_CLICK_ID: 'cdp_email_click_id',
} as const;

// =============================================================================
// LIBRARY INFO
// =============================================================================

export const SDK_NAME = '@cdp/sdk';
/**
 * Reported as `context.library.version` on every event, so it is the number we
 * see in the event stream — treat it as authoritative and keep
 * packages/sdk/package.json equal to it when bumping. (They drifted once: the
 * package stayed at the v1 `0.1.0` after the v2 rewrite.)
 */
// 2.1.0 (2026-09-21): the lean-context / collectTags / leanProperties controls
// (SDK-EVENT-PIPELINE-2026-09-21, waves 1-2). Bumped because `context.library
// .version` is the ONLY way to tell, from the stream, which bundle a browser
// is running — and that is what makes "how much of the traffic has migrated"
// measurable before a flag is turned on for a live site. Note the lean context
// deliberately drops `library`, so the version is visible on the first event
// of each page view, which is exactly where the question is asked.
// 2.3.0 (2026-10-20): no device fingerprint in any mode, and an implied page with a
// TCF CMP waits for its answer (monfri ЛО-13, LA-05/LA-06) — the version tells which
// browsers still run a bundle that fingerprints.
// 2.3.1 (2026-10-22): the Assistant preview token is read from the fragment too and leaves
// the address before the first page view (LA-16) — full bundle only.
// 2.3.2 (2026-10-26): nothing is sent from a password page, and every URL leaves the page without its
// secrets — token-shaped segments, secret parameter values, e-mail addresses (Р-ID8,
// NEWS-LOGIN-IDENTITY-2026-10-26 §5.3). Every bundle.
// 2.4.0 (2026-10-26): the site's signed-in readers are recognised from the Site Profile's `identity` block
// (core/auth-identity.ts — a verified page, the signed-in marker, the login form's e-mail held server-side
// until the sign-in shows), and an e-mail is never the userId: identify(email) and a stored e-mail userId
// become the salted hash `h:<sha256>` (Р-ID6). Every bundle. NEWS-LOGIN-IDENTITY-2026-10-26 §5.
// 2.4.1 (2026-10-26, found by the live check of 2.4.0): the sign-in module waits for a TCF CMP's answer before
// it writes or sends anything (a reader who refused purposes 5/6 got the userId cookie in the moment before the
// answer); writing a domain cookie retires the host-only copy a first visit without the loader left, which made
// reset() useless on 24chasa.
// 2.4.2 (2026-10-27): on a page without a CMP of its own the sign-in module follows the reader's TCF choice stored by
// the site's CMP on another subdomain (FCCDCF / euconsent-v2, purposes 5/6); 'Auth Detected {source, stage, outcome}' — the
// sign-in funnel without a person in it (analytics consent).
export const SDK_VERSION = '2.5.0';
